import { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import { confirmEmployeeLogout } from "../../auth/employeeLogout";
import { Footer } from "../../components/Footer";
import { NotificationCenter } from "../../components/NotificationCenter";
import { Skeleton } from "../../components/Spinner";
import { useToast } from "../../components/Toast";
import { ApiError, apiGet, apiSend } from "../../lib/customFetch";
import { AbsenceRequests } from "./AbsenceRequests";
import { formatMinutter } from "./format";
import {
  HistoryCorrections,
  type HistoryCorrection,
} from "./HistoryCorrections";
import {
  afvigelseInputLabel,
  afvigelseInputVaerdi,
  type AfvigelseValg,
} from "./standard-input";
import styles from "./StandardStemplePage.module.css";

/**
 * Medarbejderens STANDARDDAG-flade (M-10). Funktionæren stempler ikke —
 * ugeskemaet er forudfyldt, og hun registrerer kun AFVIGELSER. Standarddage
 * beregnes virtuelt server-side (decision 0009); denne flade viser dem og
 * lader medarbejderen rette en enkelt dag.
 */

type Kilde = "standard" | "afvigelse" | "fravaer";
type AfvigelseType = "timer" | "fravaer" | "ekstra";

interface StandardDag {
  dato: string;
  harAktivtSkema: boolean;
  planlagtMinutter: number;
  nettoMinutter: number;
  kilde: Kilde;
  afvigelseType: AfvigelseType | null;
  note: string | null;
  fravaerType?: "ferie" | "sygdom" | "fri";
  fravaerNote?: string | null;
}

interface StandardUge {
  ugeStart: string;
  dage: StandardDag[];
  totalNettoMinutter: number;
  totalPlanlagtMinutter: number;
  godkendt: boolean;
  aeldsteUge: string;
  rettelser: HistoryCorrection[];
  naesteRettelsesCursor: string | null;
}

const dagFmt = new Intl.DateTimeFormat("da-DK", {
  weekday: "long",
  day: "numeric",
  month: "short",
});

const KILDE_TEKST: Record<Kilde, string> = {
  standard: "Standarddag",
  afvigelse: "Afvigelse",
  fravaer: "Fravær/fri",
};

const FRAVAER_TEKST = {
  ferie: "Ferie",
  sygdom: "Sygdom",
  fri: "Fri",
} as const;

function flytUge(ugeStart: string, uger: number): string {
  const d = new Date(`${ugeStart}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + uger * 7);
  return d.toISOString().slice(0, 10);
}

export function StandardStemplePage({
  navn,
  firma,
  koerselAktiv,
  fravaerTilladt,
  enabledFeatureKeys,
}: {
  navn: string;
  firma: string;
  koerselAktiv: boolean;
  fravaerTilladt: boolean;
  enabledFeatureKeys: readonly string[];
}) {
  const toast = useToast();
  const [, navigate] = useLocation();
  const [uge, setUge] = useState<string | null>(null);
  const [data, setData] = useState<StandardUge | null>(null);
  const [fejl, setFejl] = useState(false);
  const [aaben, setAaben] = useState<string | null>(null);
  const [henterFlere, setHenterFlere] = useState(false);
  const [sideFejl, setSideFejl] = useState(false);

  const hent = useCallback(() => {
    const query = uge === null ? "" : `?uge=${uge}`;
    apiGet<StandardUge>(`/api/clockin/standard/week${query}`)
      .then((d) => {
        setData(d);
        setFejl(false);
        setSideFejl(false);
      })
      .catch(() => setFejl(true));
  }, [uge]);

  useEffect(() => {
    hent();
  }, [hent]);

  if (fejl) {
    return (
      <div className={styles.page}>
        <p className={styles.tom}>Kunne ikke hente din uge — tjek forbindelsen.</p>
        <button type="button" className={styles.retryKnap} onClick={hent}>
          Prøv igen
        </button>
      </div>
    );
  }
  if (data === null) {
    return (
      <div className={styles.page}>
        <Skeleton width="12rem" height="2rem" />
        <Skeleton width="100%" height="14rem" />
      </div>
    );
  }

  const erIndevaerende = uge === null;
  const foersteSkemadag = data.dage.find((dag) => dag.harAktivtSkema)?.dato;
  const viserDageFoerSkema = data.dage.some((dag) => !dag.harAktivtSkema);
  const hentFlere = () => {
    if (data.naesteRettelsesCursor === null || henterFlere) return;
    const params = new URLSearchParams({
      limit: "25",
      cursor: data.naesteRettelsesCursor,
    });
    if (uge !== null) params.set("uge", uge);
    setHenterFlere(true);
    setSideFejl(false);
    apiGet<StandardUge>(`/api/clockin/standard/week?${params.toString()}`)
      .then((next) => {
        setData((current) =>
          current === null
            ? next
            : {
                ...next,
                rettelser: [...current.rettelser, ...next.rettelser],
              },
        );
      })
      .catch(() => setSideFejl(true))
      .finally(() => setHenterFlere(false));
  };

  return (
    <div className={styles.page}>
      <a className={styles.skipLink} href="#main-content">
        Spring til hovedindhold
      </a>
      <header className={styles.top}>
        <div className={styles.brand}>
          app<span>smeden</span>
        </div>
        <div className={styles.hilsen}>
          <p className={styles.navn}>{navn}</p>
          <p className={styles.firma}>{firma}</p>
        </div>
        <nav className={styles.kontoNav} aria-label="Konto">
          <NotificationCenter enabledFeatureKeys={enabledFeatureKeys} />
          {koerselAktiv && <Link href="/koersel">Kørsel</Link>}
          <Link href="/stemple/enheder">Enheder</Link>
          <button
            type="button"
            onClick={() => {
              if (!confirmEmployeeLogout()) return;
              void apiSend("POST", "/api/logout").finally(() =>
                navigate("/login"),
              );
            }}
          >
            Log ud
          </button>
        </nav>
      </header>

      <main id="main-content" className={styles.main} tabIndex={-1}>
      <h1 className={styles.srOnly}>Min standarduge</h1>
      <div className={styles.ugeHeader}>
        <button
          type="button"
          className={styles.navKnap}
          aria-label="Forrige uge"
          disabled={data.ugeStart <= data.aeldsteUge}
          onClick={() => setUge(flytUge(data.ugeStart, -1))}
        >
          ‹
        </button>
        <button
          type="button"
          className={styles.navLabel}
          onClick={() => setUge(null)}
          disabled={erIndevaerende}
        >
          {erIndevaerende
            ? "Denne uge"
            : `Uge fra ${new Intl.DateTimeFormat("da-DK", { day: "numeric", month: "short" }).format(new Date(`${data.ugeStart}T00:00:00`))}`}
        </button>
        <button
          type="button"
          className={styles.navKnap}
          aria-label="Næste uge"
          onClick={() => setUge(flytUge(data.ugeStart, 1))}
          disabled={erIndevaerende}
        >
          ›
        </button>
      </div>

      {data.godkendt && (
        <p className={styles.laast} role="status">
          🔒 Ugen er godkendt af chefen — afvigelser kan ikke rettes.
        </p>
      )}

      {viserDageFoerSkema && foersteSkemadag !== undefined && (
        <p role="note" className={styles.skemaStart}>
          Dit skema gælder fra {dagFmt.format(new Date(`${foersteSkemadag}T00:00:00`))} —
          tidligere dage i denne uge er derfor ikke med.
        </p>
      )}

      {fravaerTilladt && <AbsenceRequests />}

      <ul className={styles.liste}>
        {data.dage.map((d) => (
          <li key={d.dato}>
            <button
              type="button"
              className={styles.dagKnap}
              disabled={
                data.godkendt ||
                !d.harAktivtSkema ||
                d.fravaerType !== undefined
              }
              onClick={() => setAaben(aaben === d.dato ? null : d.dato)}
            >
              <span className={styles.dagInfo}>
                <span className={styles.dagDato}>
                  {dagFmt.format(new Date(`${d.dato}T00:00:00`))}
                </span>
                <span
                  className={`${styles.kilde} ${d.fravaerType !== undefined || d.kilde === "fravaer" ? styles.kildeFravaer ?? "" : d.kilde === "standard" ? styles.kildeStandard ?? "" : styles.kildeAfvigelse ?? ""}`}
                >
                  {d.fravaerType === undefined
                    ? d.harAktivtSkema
                      ? KILDE_TEKST[d.kilde]
                      : "Ikke i skema endnu"
                    : `${FRAVAER_TEKST[d.fravaerType]}${d.fravaerNote ? ` · ${d.fravaerNote}` : ""}`}
                </span>
              </span>
              <span className={styles.dagNetto}>
                {d.harAktivtSkema ? formatMinutter(d.nettoMinutter) : "—"}
              </span>
            </button>
            {aaben === d.dato && !data.godkendt && d.fravaerType === undefined && (
              <AfvigelseEditor
                dag={d}
                onDone={() => {
                  setAaben(null);
                  hent();
                }}
                onError={(besked) => toast.show(besked, "error")}
              />
            )}
          </li>
        ))}
      </ul>

      <p className={styles.total}>
        Uge i alt: <strong>{formatMinutter(data.totalNettoMinutter)}</strong>
      </p>

      <HistoryCorrections
        rettelser={data.rettelser}
        naesteCursor={data.naesteRettelsesCursor}
        henterFlere={henterFlere}
        sideFejl={sideFejl}
        onHentFlere={hentFlere}
      />
      </main>

      <Footer variant="lys" />
    </div>
  );
}

function AfvigelseEditor({
  dag,
  onDone,
  onError,
}: {
  dag: StandardDag;
  onDone: () => void;
  onError: (besked: string) => void;
}) {
  const startValg: AfvigelseValg =
    dag.afvigelseType === "timer"
      ? "timer"
      : dag.afvigelseType === "ekstra"
          ? "ekstra"
          : "skema";
  const [valg, setValg] = useState<AfvigelseValg>(startValg);
  const [timer, setTimer] = useState(
    afvigelseInputVaerdi(
      startValg === "ekstra" ? "ekstra" : "timer",
      dag.afvigelseType,
      dag.planlagtMinutter,
      dag.nettoMinutter,
    ),
  );
  const [note, setNote] = useState(dag.note ?? "");
  const [busy, setBusy] = useState(false);

  const gem = () => {
    setBusy(true);
    const minutter = Math.round(Number(timer.replace(",", ".")) * 60);
    const kald =
      valg === "skema"
        ? apiSend("DELETE", `/api/clockin/standard/afvigelse/${dag.dato}`)
        : apiSend("PUT", "/api/clockin/standard/afvigelse", {
            dato: dag.dato,
            type: valg,
            minutter,
            note: note.trim().length > 0 ? note.trim() : null,
          });
    kald
      .then(onDone)
      .catch((err: unknown) => {
        setBusy(false);
        if (err instanceof ApiError && err.status === 409) {
          onError("Ugen er godkendt og låst.");
          onDone();
        } else if (err instanceof ApiError && err.status === 404) {
          // "Følg skema" på en dag uden afvigelse — allerede som ønsket.
          onDone();
        } else {
          onError("Kunne ikke gemme — prøv igen.");
        }
      });
  };

  const visTimer = valg === "timer" || valg === "ekstra";
  const vaelg = (naesteValg: AfvigelseValg) => {
    setValg(naesteValg);
    if (naesteValg !== "skema") {
      setTimer(
        afvigelseInputVaerdi(
          naesteValg,
          dag.afvigelseType,
          dag.planlagtMinutter,
          dag.nettoMinutter,
        ),
      );
    }
  };

  return (
    <div className={styles.editor}>
      <div className={styles.valgRaekke}>
        {(
          [
            ["skema", "Følg skema"],
            ["timer", "Andet timetal"],
            ["ekstra", "Ekstra tid"],
          ] as [AfvigelseValg, string][]
        ).map(([v, label]) => (
          <button
            key={v}
            type="button"
            className={`${styles.valg} ${valg === v ? styles.valgAktiv ?? "" : ""}`}
            onClick={() => vaelg(v)}
          >
            {label}
          </button>
        ))}
      </div>
      {visTimer && (
        <label className={styles.timerFelt}>
          {afvigelseInputLabel(valg)}
          <input
            type="number"
            inputMode="decimal"
            min="0"
            max="24"
            step="0.1"
            value={timer}
            onChange={(e) => setTimer(e.target.value)}
          />
        </label>
      )}
      <textarea
        className={styles.noteFelt}
        rows={2}
        maxLength={500}
        placeholder="Note (valgfri)"
        aria-label="Note til afvigelsen"
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
      <button
        type="button"
        className={styles.gemKnap}
        disabled={busy}
        onClick={gem}
      >
        {busy ? "Gemmer…" : "Gem"}
      </button>
    </div>
  );
}
